import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { CreditCard } from 'lucide-react';

const COLORS = ['#01796d', '#3b82f6', '#ec4899', '#f59e0b', '#8b5cf6', '#10b981', '#ef4444', '#6366f1'];

export function PaymentModeChart({ data }) {
  
  // Process the payment mode data
  const processPaymentData = (inputData) => {
    if (!inputData) return [];

    if (Array.isArray(inputData) && inputData.length > 0) {

      const filteredData = inputData.filter(item => 
        (item.count || item.OrderCount || 0) > 0
      );
      
      const totalAmount = filteredData.reduce((sum, item) => 
        sum + (item.amount || item.TotalAmount || 0), 0
      );
      
      return filteredData.map(item => ({
        name: item.mode || item.PaymentMode || 'Unknown',
        amount: item.amount || item.TotalAmount || 0,
        orders: item.count || item.OrderCount || 0,
        percentage: totalAmount > 0 
          ? ((item.amount || item.TotalAmount || 0) / totalAmount) * 100 
          : 0
      }));
    }

    return [];
  };

  const processedData = processPaymentData(data);
  
  // If no data after processing, show empty state
  if (processedData.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Payment Modes</h3>
            <p className="text-sm text-slate-500 mt-1">Revenue by payment type</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl shadow-lg">
            <CreditCard className="w-5 h-5 text-white" />
          </div>
        </div>
        <div className="h-[300px] flex items-center justify-center">
          <div className="text-center">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No payment mode data available</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate total for summary
  const totalAmount = processedData.reduce((sum, item) => sum + item.amount, 0);
  const totalOrders = processedData.reduce((sum, item) => sum + item.orders, 0);

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Payment Modes</h3>
          <p className="text-sm text-slate-500 mt-1">Revenue by payment type</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl shadow-lg">
          <CreditCard className="w-5 h-5 text-white" />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Total Revenue</p>
          <p className="text-lg font-bold text-blue-700">
            Rs. {totalAmount.toLocaleString(undefined, { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </p>
        </div>
        <div className="p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Total Orders</p>
          <p className="text-lg font-bold text-purple-700">{totalOrders}</p>
        </div>
      </div>

      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={processedData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
              outerRadius={100}
              innerRadius={60}
              fill="#8884d8"
              dataKey="amount"
              strokeWidth={3}
              stroke="#fff"
            >
              {processedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                padding: '8px 12px'
              }}
              formatter={(value, name, props) => {
                const item = props.payload;
                return [
                  <div key="tooltip">
                    <div className="font-semibold text-slate-900">
                      Rs. {item.amount.toLocaleString(undefined, { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {item.orders} {item.orders === 1 ? 'order' : 'orders'} • {item.percentage.toFixed(1)}%
                    </div>
                  </div>,
                  item.name
                ];
              }}
            />
            <Legend 
              layout="horizontal" 
              verticalAlign="bottom" 
              align="center"
              wrapperStyle={{ paddingTop: '20px' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4">
        {processedData.map((item, index) => (
          <div 
            key={index} 
            className="flex items-center justify-between p-3 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full shadow-md"
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              ></div>
              <div>
                <span className="text-xs font-medium text-slate-700 block">{item.name}</span>
                <span className="text-xs text-slate-500">{item.orders} orders</span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-bold text-slate-900">
                Rs. {item.amount.toLocaleString()}
              </span>
              <span className="text-xs text-[#01796d] block">
                {item.percentage.toFixed(1)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}